import * as React from 'react'

import {
  Image as ImageComponent,
  SafeHtml,
} from '@thg-commerce/gravity-elements'
import { styled } from '@thg-commerce/enterprise-theme'

import {
  HeaderElementNode,
  ImageElementNode,
  LinkElementNode,
  ListElementNode,
  NodeChildren,
  TextElementNode,
  TableElementNode,
} from '../../../post-content'
import { RendererContext } from './types'

export const Table = ({ content }: TableElementNode['props']) => (
  <SafeHtml
    __dangerouslyAllowedTags={[
      'table, caption, colgroup, col, th, thead, tbody, tr, td, tfoot',
      'strong',
      'em',
    ]}
    content={content}
  />
)

const listTypeMap: { [type in ListElementNode['props']['type']]: string } = {
  ordered: 'ol',
  unordered: 'ul',
}

export const Image = ({
  alt = '',
  width = 'auto',
  height = 'auto',
  context,
  ...props
}: ImageElementNode['props'] & {
  isAmp: boolean
  context: RendererContext
}) => <ImageComponent {...props} alt={alt} width={width} height={height} />

export const Link = ({
  uri,
  children,
  target,
  rel,
}: LinkElementNode['props'] & { children: any }) => (
  <a target={target ? `_${target}` : undefined} rel={rel} href={uri}>
    {children}
  </a>
)

export const Header = ({
  level,
  children,
  id,
}: HeaderElementNode['props'] & {
  context: RendererContext
  children: any
}) => {
  return React.createElement(`h${level}`, { id }, children)
}

export const List = ({
  type,
  items,
  context,
  nodeRenderer,
  id,
}: ListElementNode['props'] & {
  context: RendererContext
  nodeRenderer: (
    nodes: NodeChildren[],
    context: RendererContext,
  ) => React.ReactNode[]
}) => {
  const tagName = listTypeMap[type]
  return React.createElement(
    tagName,
    { id },
    items.map((item, index) => (
      <li key={index}>{nodeRenderer([item], context)}</li>
    )),
  )
}

const StyledText = styled.span<{
  weight: TextElementNode['props']['weight']
  fontStyle: TextElementNode['props']['style']
}>`
  ${(props) =>
    props.weight === 'bold' &&
    `font-weight: ${props.theme.typography.bodyText.alternateFontStyle.fontWeight};`}
  ${(props) => props.fontStyle === 'italic' && 'font-style: italic;'}
  ${(props) => props.fontStyle === 'underline' && 'text-decoration: underline;'}
`

export const Text = ({
  children,
  weight,
  style,
  id,
}: Omit<TextElementNode['props'], 'text'> & { children: string }) =>
  (weight && weight !== 'normal') || style || id ? (
    <StyledText weight={weight} fontStyle={style} id={id}>
      {children}
    </StyledText>
  ) : (
    children
  )
